export const PHONE_STATE = {
  idle: 'idle',
  connecting: 'connecting',
  ready: 'ready',
  error: 'error',
  offline: 'offline',
} as const

export type PhoneState = (typeof PHONE_STATE)[keyof typeof PHONE_STATE]

// Why the phone left `ready` without the user asking it to. Keys double as the
// i18n leaves under `telephony.reason.*`; a manual disconnect never sets one.
export const DISCONNECT_REASON = {
  socketClosed: 'socketClosed',
  socketError: 'socketError',
  clientError: 'clientError',
  connectFailed: 'connectFailed',
  networkOffline: 'networkOffline',
  retriesExhausted: 'retriesExhausted',
} as const

export type DisconnectReason =
  (typeof DISCONNECT_REASON)[keyof typeof DISCONNECT_REASON]

// Normalised call lifecycle the UI consumes, decoupled from the Telnyx SDK's
// own state machine (new/requesting/ringing/active/held/hangup/destroy).
export const CALL_SESSION_STATE = {
  init: 'init',
  ringing: 'ringing',
  answered: 'answered',
  disposed: 'disposed',
  failed: 'failed',
} as const

export type CallSessionState =
  (typeof CALL_SESSION_STATE)[keyof typeof CALL_SESSION_STATE]

// Wall-clock timestamps stamped the moment each Telnyx transition fires, so the
// connect time / duration is exact rather than measured at React render time.
export interface CallTimings {
  ringingAt?: number
  // When the agent hit answer (start of pickup), stamped before any await so the
  // diagnostics probe can measure dead air from the moment of pickup, not from
  // when the call later reaches the active state.
  answerInitiatedAt?: number
  answeredAt?: number
  connectedAt?: number
  endedAt?: number
}

/** One quality band, shared by the end-of-call report and the live indicator.
 *  `unknown` = not enough samples to judge (call too young, or on hold). */
export type CallQuality = 'good' | 'fair' | 'poor' | 'unknown'

/**
 * The live in-call connection-health reading shown to the agent, refreshed
 * every watchdog tick over a short rolling window (so it recovers when the
 * network does). `mine` is a true measurement of the agent leg (browser ↔
 * Telnyx: loss/jitter/RTT); `theirs` is an inference from what the far side's
 * audio stream actually carries — the customer's own network is invisible to
 * WebRTC stats, so it never reports `fair`, only flowing/broken/unknown.
 */
export interface LiveCallQuality {
  mine: CallQuality
  theirs: CallQuality
  /** Agent-leg detail for the tooltip; absent while unmeasurable. */
  lossPct?: number
  jitterMs?: number
  rttMs?: number
}

export const CALL_DIRECTION = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const

export type CallDirection = (typeof CALL_DIRECTION)[keyof typeof CALL_DIRECTION]

export const CALL_SESSION_EVENT = {
  ringing: 'ringing',
  answered: 'answered',
  disposed: 'disposed',
  failed: 'failed',
} as const

export type CallSessionEvent =
  (typeof CALL_SESSION_EVENT)[keyof typeof CALL_SESSION_EVENT]

// A single live call, normalised over the Telnyx native Call so the softphone,
// power dialer and active-call pills never touch the SDK directly.
export interface TelephonyCall {
  readonly callId: string
  readonly direction: CallDirection
  readonly remoteNumber: string
  readonly state: CallSessionState
  /**
   * True when this is the power dialer's bridged agent leg — the incoming
   * INVITE carried the `X-Powerdial: 1` SIP header. The header is the only
   * reliable signal (caller id is not); it drives auto-answer and keeps the
   * regular incoming-call UI/ringtone away from dialer calls.
   */
  readonly powerdial: boolean
  /**
   * The remote party's incoming audio, once call media is up. Present only on
   * the leader tab's real calls — mirrored follower calls carry no media —
   * so consumers (e.g. the live transcript) must tolerate `undefined`.
   */
  readonly remoteStream?: MediaStream | undefined
  on: (event: CallSessionEvent, handler: () => void) => void
  off: (event: CallSessionEvent, handler: () => void) => void
  answer: () => void
  decline: () => Promise<void>
  hangup: () => Promise<void>
  cancel: () => Promise<void>
  dispose: () => void
  /** True while the agent's mic is muted. Lives on the session (not per-tab UI
   *  state) so every tab's controls show the same pressed state. */
  readonly muted: boolean
  /** True while the call is parked on hold. Session-level for the same reason. */
  readonly held: boolean
  mute: () => void
  unmute: () => void
  hold: () => Promise<void>
  unhold: () => Promise<void>
  /** Send a DTMF digit (`0-9`, `*`, `#`) into the live call. */
  dtmf: (digit: string) => void
}
